import { gql } from "apollo-boost";

import {
  Citations,
  ConversationDDFragment,
  ConversationNextSuggestionsFragment,
  DebugSheet,
  Feature,
  Flag,
  GroupedFeatureResponse,
  MeasurementRange,
  PatientSuggestionMIExplanationsFragment,
  PatientSuggestions,
  QualifiedFeature,
  Qualifier,
} from "./shared";
import { GroundingFragment } from "./grounding";

const Category = gql`
  fragment Category on Feature {
    name
    id
    shortname
  }
`;

const Panel = gql`
  fragment Panel on Panel {
    name
    id
    shortname
    category {
      ...Category
    }
    panelGroup {
      ...Category
    }
    panelSubgroup {
      ...Category
    }
    findings {
      ...QualifiedFeature
    }
  }
  ${QualifiedFeature}
  ${Category}
`;

const PatientFirstSummaryFragment = gql`
  fragment PatientFirstSummary on SummeryData {
    age
    gender
    main
    visitReason {
      id
      name
    }
    sections {
      content
      title
      type
    }
  }
`;

const ConversationSuggestionFragment = gql`
  fragment ConversationSuggestion on PatientSuggestion {
    questionType
    suggestion {
      ...QualifiedFeature
    }
    refinements {
      ...QualifiedFeature
    }
    entropy
    entropyYes
    entropyNo
    mutualInformation
    mutualInformationUrgencyDifferentiation
    expectation
    ...PatientSuggestionMIExplanations @include(if: $debug)
  }
  ${PatientSuggestionMIExplanationsFragment}
  ${QualifiedFeature}
`;

const ShareCodeDataFragment = gql`
  fragment ShareCodeDataFragment on ShareCodeData {
    id
    date
    shareid
    seen
    digitCode
    comment
  }
`;

const visitStatusFragment = gql`
  fragment visitStatusFragment on VisitStatusStructure {
    age
    gender
    status
    visitReason {
      id
      name
    }
    chiefComplaint {
      id
      name
      shortname
    }
  }
`;

const CreateCodeDataFragment = gql`
  fragment CreateCodeDataFragment on CreatedCode {
    id
    ehrid
    shareid
    digitCode
  }
`;

const ConversationRecommendationFragment = gql`
  fragment ConversationRecommendation on PolicyRecommendation {
    feature {
      ...QualifiedFeature
    }
    policyName
    policyid
    protocolid
    protocolName
  }
  ${QualifiedFeature}
`;
const ConversationInfoMessageFragment = gql`
  fragment ConversationInfoMessage on ConversationInfoMessage {
    id
    infoText
    type
    confirm
    confirmText
  }
  ${QualifiedFeature}
`;

const ConversationExchangeResultFragment = gql`
  fragment ConversationExchangeResult on ConversationExchange {
    question {
      questionType
      suggestions {
        ...ConversationSuggestion
      }
      basedOnReasoning
    }
    answer {
      suggestionId
      refinementId
      patientAnswer
      finding {
        id
        name
        negate
        skip
        feature {
          ...Feature
        }
        qualifiers {
          source
          name
          relation {
            name
            id
            type
            multi
          }
          value {
            name
            shortname
            id
            category {
              id
              name
              shortname
            }
            range @include(if: $includeFeatureRange) {
              ...MeasurementRange
            }
            parents @skip(if: $qfLight) {
              features {
                id
                name
                childrenSetType
              }
            }
            patientDescription
            description {
              text
            }
          }
          negate
          range {
            ...MeasurementRange
          }
          freeText
        }
        mandatory {
          relation {
            name
            id
            type
          }
          value {
            name
            id
          }
          range {
            ...MeasurementRange
          }
          freeText
        }
      }
    }
    state {
      context
      entropy
      mutualInformation
      caseId
      nextSuggestions {
        diseases {
          disease {
            id
            name
          }
          prob
        }
        suggestions {
          perDiseaseMetrics {
            suggestion {
              id
            }
            disease {
              id
            }
            findingGivenDisease
          }
          suggestion {
            id
            name
            qualifiers {
              name
              relation {
                id
              }
              range {
                low
                high
                units
              }
            }
          }
          findingEntropy
          findingLeak
          findingProbability
          category
          mutualInformation
          mutualInformationUrgencyDifferentiation
        }
      }

      diagnosis {
        diseaseGivenDependentFindings
        urgency
        disease {
          name
          feature {
            id
            parents {
              features {
                id
              }
            }
          }
        }
      }
    }
  }
  ${ConversationSuggestionFragment}
  ${Feature}
  ${MeasurementRange}
`;

const ConversationNextQuestionFragment = gql`
  fragment ConversationNextQuestion on ConversationQuestion {
    id
    questionType
    questionChoiceType
    description @include(if: $debug)
    debugInfo @include(if: $debug)
    elapsed
    question {
      text
    }
    suggestions {
      questionType
      canBeUnknown
      refinementQuestionType
      refinementLocationRange {
        id
        name
      }
      question {
        text
      }
      suggestion {
        ...QualifiedFeature
      }
      refinements {
        ...QualifiedFeature
      }
      mutualInformation
      mutualInformationUrgencyDifferentiation
      ...PatientSuggestionMIExplanations @include(if: $debug)
    }
    basedOnReasoning
  }
  ${QualifiedFeature}
  ${Qualifier}
  ${PatientSuggestionMIExplanationsFragment}
`;

const ConversationExchangeFragment = gql`
  fragment ConversationExchange on ConversationExchange {
    question {
      ...ConversationNextQuestion
    }
    answer {
      suggestionId
      refinementId
      patientAnswer
      finding {
        ...QualifiedFeature
      }
      findingAlias
    }
    messages {
      ...ConversationInfoMessage
    }
    patientAnswer
    progress
  }
  ${ConversationNextQuestionFragment}
  ${ConversationInfoMessageFragment}
  ${QualifiedFeature}
`;

const ConversationStateFragment = gql`
  fragment ConversationState on ConversationState {
    gender
    progress
    entropy @include(if: $debug)
    context
    caseId
    caseReadOnly @include(if: $debug)
    caseBasedOn @include(if: $debug)
    initialMessages {
      ...ConversationInfoMessage
    }
    urgent @include(if: $debug) {
      urgencyLevelId
    }
    nextSuggestions @include(if: $debug) {
      ...ConversationNextSuggestions
    }
    ddClusters @include(if: $debug) {
      ...ConversationDD
      debugReport
    }
    recommendations @include(if: $debug) {
      ...ConversationRecommendation
    }
    navigationRecommendation {
      ...ConversationRecommendation
    }
    patientFindings @include(if: $debug) {
      ...GroupedFeatureResponse
    }
    patientIcd @include(if: $debug) {
      diseases {
        code
        desc
        dueTo {
          ...QualifiedFeature
        }
      }
      findings {
        code
        desc
        dueTo {
          ...QualifiedFeature
        }
      }
    }
    options @include(if: $debug)
  }
  ${ConversationNextSuggestionsFragment}
  ${PatientSuggestionMIExplanationsFragment}
  ${QualifiedFeature}
  ${ConversationDDFragment}
  ${ConversationRecommendationFragment}
  ${GroupedFeatureResponse}
  ${ConversationInfoMessageFragment}
`;

const ConversationStateNextQFragment = gql`
  fragment ConversationStateNextQ on ConversationState {
    nextQuestion {
      ...ConversationNextQuestion
    }
    ...ConversationState
  }
  ${ConversationStateFragment}
  ${ConversationNextQuestionFragment}
`;

const ConversationStateNextResponseFragment = gql`
  fragment ConversationStateNextResponse on ConversationState {
    nextResponse {
      question {
        ...ConversationNextQuestion
      }
      messages {
        ...ConversationInfoMessage
      }
    }
    ...ConversationState
  }
  ${ConversationInfoMessageFragment}
  ${ConversationNextQuestionFragment}
  ${ConversationStateFragment}
`;

const GeneratedOutputFragment = gql`
  fragment GeneratedOutput on GeneratedOutput {
    generating
    error
    errorText
    etag
  }
`;

const SummaryTextFragment = gql`
  fragment SummaryText on SummaryText {
    text
    ...GeneratedOutput
  }
  ${GeneratedOutputFragment}
`;

const GroundedSummaryTextFragment = gql`
  fragment GroundedSummaryText on GroundedSummaryText {
    ...SummaryText
    grounding {
      grounding {
        ...Grounding
      }
      ...GeneratedOutput
    }
  }
  ${GroundingFragment}
  ${SummaryTextFragment}
  ${GeneratedOutputFragment}
`;

const LoadedModelInfoFragment = gql`
  fragment LoadedModelInfo on LoadedModelInfo {
    commitSerial
    revision
  }
  fragment BackendModelInfo on Query {
    gatewayModel @include(if: $debug) {
      ...LoadedModelInfo
    }
    diagnosisModel @include(if: $debug) {
      ...LoadedModelInfo
    }
  }
`;

const SummaryDiseaseCluster = gql`
  fragment SummaryDiseaseCluster on SummaryDiseaseCluster {
    title {
      ...QualifiedFeature
    }
    inTopCRPerSortType
    scores {
      sortType
      adjustedNormalized
      postClusterAdjustment
    }
    icd {
      code
      desc
    }
    explanation {
      ...GroundedSummaryText
    }
  }
  ${QualifiedFeature}
  ${GroundedSummaryTextFragment}
`;

const SummaryProblem = gql`
  fragment SummaryProblem on SummaryProblem {
    problem {
      ...QualifiedFeature
    }
    icd {
      code
      desc
    }
  }
  ${QualifiedFeature}
`;

export const Web = {
  UserRoles: gql`
    query GetUserRoles {
      getUserRoles
    }
  `,
  DDOverviewQuery: gql`
    query DDOverview(
      $qfs: [QualifiedFeatureInput]
      $consider: [QualifiedFeatureInput]
      $pathOptions: PathOptions
      $diagnosisOptions: DiagnosisOptionsInput
      $depth: Int
      $limit: Int
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      dd(
        qualifiedFeatures: $qfs
        depth: $depth
        pathOptions: $pathOptions
        diagnosisOptions: $diagnosisOptions
        limit: $limit
        conditions: []
        diseasesToConsider: $consider
      ) {
        citations
        clinicalReasoning {
          id
          disease {
            ...QualifiedFeature
          }
          inInitialDisorders
          isKahunDisorder
          forcedInBestMatch
          forcedInHighestProb
          forcedInSort
          score
          urgency
          diseaseGivenIndependentFindings
          diseaseGivenDependentFindings
          liftWithDependentFindings
          prevalence
          prevalenceDescription
          incidence
          incidenceDescription
          isRedundant
          flags {
            ...Flag
          }
        }
      }
    }
    ${Flag}
    ${QualifiedFeature}
  `,
  DDWithCaseQuery: gql`
    query DDWithCase(
      $qfs: [GroupedFeature]
      $considerPanels: [String]
      $diseasesToDiscard: [QualifiedFeatureInput]
      $diseasesToAdd: [QualifiedFeatureInput]
      $nextStepDiseasesToConsider: [QualifiedFeatureInput]
      $pathOptions: PathOptions
      $diagnosisOptions: DiagnosisOptionsInput
      $depth: Int
      $limit: Int
      $withDebug: Boolean!
      $adaptiveRatio: Float
      $qfLight: Boolean = true
      $sortTypes: [SortType]
      $conditions: [CaseConditionInput] = []
      $backEndMode: Boolean = false
      $includeCitations: Boolean = true
      $includeFeatureRange: Boolean = false
      $caseId: String = null
    ) {
      dd(
        patientCaseInput: { findings: $qfs }
        depth: $depth
        pathOptions: $pathOptions
        diagnosisOptions: $diagnosisOptions
        limit: $limit
        conditions: $conditions
        panelsToConsider: $considerPanels
        diseasesToDiscard: $diseasesToDiscard
        diseasesToAdd: $diseasesToAdd
        nextStepDiseasesToConsider: $nextStepDiseasesToConsider
        adaptiveRatio: $adaptiveRatio
        sortTypes: $sortTypes
        includeCitations: $includeCitations
        caseId: $caseId
      ) {
        allFindings {
          ...QualifiedFeature
        }
        citations
        clinicalReasoning {
          id
          disease {
            ...QualifiedFeature
            urgencyInfo: feature {
              id
              urgency
              urgencyId
            }
          }
          diseaseCitations @include(if: $includeCitations) {
            ...Citations
          }
          scores {
            sortType
            adjusted
            postClusterAdjustment
          }
          findings {
            finding {
              id
            }
            sourceQFIndex
            absolutePredictiveValue
            likelihoodRatio
          }
          subTypes {
            ...QualifiedFeature
          }
          inInitialDisorders @include(if: $backEndMode)
          isKahunDisorder @include(if: $backEndMode)
          forcedInBestMatch @include(if: $backEndMode)
          forcedInHighestProb @include(if: $backEndMode)
          forcedInSort @include(if: $backEndMode)
          missingPresentingSymptoms @include(if: $backEndMode)
          presentableDisease @include(if: $backEndMode)
          score
          urgency
          diseaseGivenIndependentFindings
          diseaseGivenDependentFindings
          liftWithDependentFindings @include(if: $backEndMode)
          prevalence @include(if: $backEndMode)
          prevalenceDescription @include(if: $backEndMode)
          incidence @include(if: $backEndMode)
          incidenceDescription @include(if: $backEndMode)
          isRedundant @include(if: $backEndMode)
          inTopCR
          inTopCRPerSortType
          patientIncidence
          flags {
            ...Flag
          }
          redundantDueToBySort @include(if: $backEndMode) {
            sortType
            redundantDueTo {
              ...QualifiedFeature
            }
          }
          matchedRankings @include(if: $backEndMode) {
            matchType
            condition {
              sortType
              ddMode
              feature {
                id
              }
            }
          }
        }
        meetsConditions @include(if: $backEndMode) {
          condition {
            feature {
              id
              name
            }
            type
            sortType
            ddMode
            payload {
              feature {
                id
                name
              }
              val
            }
          }
          meets
        }
        strength
        strengthForSortType {
          sortType
          strength
        }
        clustersToConsider {
          id
          crIds
          sortType
          title {
            ...QualifiedFeature
            urgencyInfo: feature {
              id
              urgency
              urgencyId
            }
          }
          bestCrId
          flags
          citations {
            ...Citations
          }
          urgencyId
          scores {
            adjustedNormalized
          }
        }
        debugSheet @include(if: $withDebug) {
          rows {
            cols
          }
        }
        debugReport @include(if: $withDebug)
        debugSheets @include(if: $withDebug) {
          ...DebugSheet
        }
        urgencyBySortType {
          sortType
          urgencyLevelId
        }
        options
      }
    }
    ${Flag}
    ${QualifiedFeature}
    ${Citations}
    ${DebugSheet}
  `,
  DDReportOnlyQuery: gql`
    query DDReportOnly(
      $qfs: [GroupedFeature]
      $considerPanels: [String]
      $diseasesToDiscard: [QualifiedFeatureInput]
      $diseasesToAdd: [QualifiedFeatureInput]
      $nextStepDiseasesToConsider: [QualifiedFeatureInput]
      $pathOptions: PathOptions
      $diagnosisOptions: DiagnosisOptionsInput
      $depth: Int
      $limit: Int
      $adaptiveRatio: Float
      $sortTypes: [SortType]
      $conditions: [CaseConditionInput] = []
    ) {
      dd(
        patientCaseInput: { findings: $qfs }
        depth: $depth
        pathOptions: $pathOptions
        diagnosisOptions: $diagnosisOptions
        limit: $limit
        conditions: $conditions
        panelsToConsider: $considerPanels
        diseasesToDiscard: $diseasesToDiscard
        diseasesToAdd: $diseasesToAdd
        nextStepDiseasesToConsider: $nextStepDiseasesToConsider
        adaptiveRatio: $adaptiveRatio
        sortTypes: $sortTypes
      ) {
        debugReport
      }
    }
  `,
  Plan: gql`
    query Plan(
      $diseases: [DiseaseForPlanInput]
      $patientCase: PatientCaseInput
      $pathOptions: PathOptions
      $diagnosisOptions: DiagnosisOptionsInput
      $withDebug: Boolean!
      $qfLight: Boolean = true
      $limit: Int = 25
      $categories: [DiseaseCategoryType]
      $withDiseaseMetrics: Boolean = true
      $limitPerCategory: [PlanLimits] = []
      $diseasesAliasing: [DiseasesAliasing] = []
      $includeFeatureRange: Boolean = false
      $caseId: String = null
    ) {
      plan(
        diseases: $diseases
        patientCaseInput: $patientCase
        pathOptions: $pathOptions
        diagnosisOptions: $diagnosisOptions
        limitSuggestions: $limit
        categories: $categories
        limitPerCategory: $limitPerCategory
        diseasesAliasing: $diseasesAliasing
        caseId: $caseId
      ) {
        ...PatientSuggestions
      }
    }
    ${PatientSuggestions}
  `,
  DiseasePatientQuery: null,
  RestoreCase: gql`
    query restoreCase(
      $findings: [QualifiedFeatureInput]
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      qualifiedFeatures(qfs: $findings) {
        ...QualifiedFeature
      }
    }
    ${QualifiedFeature}
  `,
  PatientCaseMetaData: gql`
    query patientCaseMetaData($id: String) {
      patientCaseMetaData(id: $id) {
        clinicId
        etag
      }
    }
  `,
  RestorePatientCase: gql`
    query restorePatientCase(
      $id: String
      $clinicId: String = null
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      restorePatientCase(id: $id, clinicId: $clinicId) {
        findings {
          features {
            feature {
              ...QualifiedFeature
            }
            group
            source
          }
          group
        }
        data {
          payload
        }
        etag
      }
    }
    ${QualifiedFeature}
  `,
  CheckForPII: gql`
    query checkForPII($input: String) {
      checkForPII(input: $input) {
        status
        errors
      }
    }
  `,
  StorePatientCase: gql`
    mutation storePatientCase(
      $qfs: [SavedCaseGroupInput]
      $data: OpaqueDataInput
      $auto: Boolean
      $clinicId: String
      $readOnly: Boolean
    ) {
      storePatientCase(
        patientCaseInput: { findings: $qfs, data: $data }
        auto: $auto
        clinicId: $clinicId
        readOnly: $readOnly
      )
    }
  `,
  UpdatePatientCase: gql`
    mutation updatePatientCase(
      $qfs: [SavedCaseGroupInput]
      $data: OpaqueDataInput
      $id: String
      $etag: String
      $clinicId: String
    ) {
      updatePatientCase(
        patientCaseInput: { findings: $qfs, data: $data }
        id: $id
        etag: $etag
        clinicId: $clinicId
      ) {
        status
        etag
      }
    }
  `,
  UpdatePatientSummary: gql`
    mutation updatePatientSummary(
      $caseId: String
      $clinicId: String
      $summaryInput: SummaryInput
    ) {
      updateSummary(
        caseId: $caseId
        clinicId: $clinicId
        summaryInput: $summaryInput
      ) {
        status
        errors
        etag
      }
    }
  `,
  CopyPatientCase: gql`
    mutation copyPatientCase($id: String, $clinicId: String) {
      copyPatientCase(id: $id, clinicId: $clinicId)
    }
  `,
  StoreOpaqueData: gql`
    mutation storeOpaqueData($data: StoredData, $type: String, $auto: Boolean) {
      storeOpaqueData(data: $data, type: $type, auto: $auto)
    }
  `,
  UpdateOpaqueData: gql`
    mutation updateOpaqueData($id: String, $data: StoredData, $type: String) {
      updateOpaqueData(id: $id, data: $data, type: $type)
    }
  `,
  RestoreOpaqueData: gql`
    query restoreOpaqueData(
      $id: String
      $type: String
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = true
    ) {
      restoreOpaqueData(id: $id, type: $type) {
        features {
          feature {
            ...QualifiedFeature
          }
          group
          skip
        }
        data
      }
    }
    ${QualifiedFeature}
  `,
  SendFeedback: gql`
    mutation sendFeedback(
      $clinicName: String
      $rating: Int
      $text: String
      $sharedCase: String
      $sharedCaseId: String
      $urlPrefix: String
      $sender: String
    ) {
      sendFeedback(
        clinicName: $clinicName
        rating: $rating
        text: $text
        sharedCase: $sharedCase
        sharedCaseId: $sharedCaseId
        urlPrefix: $urlPrefix
        sender: $sender
      )
    }
  `,
  PathGraphQuery: null,
  PanelQuery: gql`
    query getPanel(
      $id: ID!
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      panel(id: $id) {
        ...Panel
      }
    }
    ${Panel}
  `,
  PanelChildrenQuery: gql`
    query getPanelWithChildren(
      $id: ID!
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      panel(id: $id) {
        id
        children {
          ...Panel
        }
      }
    }
    ${Panel}
  `,
  SearchQuery: gql`
    query SearchQualifiedFeaturesQuery(
      $search: String
      $categories: [ID]
      $gender: ID
      $qfLight: Boolean = false
      $patientFacing: Boolean = false
      $includeFeatureRange: Boolean = false
      $panelIds: [ID] = null
      $ancestors: [ID] = null
      $customPatientFacingPropertyValues: [String] = null
    ) {
      qualifiedFeatureSearch(
        search: $search
        limit: 6
        categories: $categories
        gender: $gender
        patientFacing: $patientFacing
        panelIds: $panelIds
        ancestors: $ancestors
        customPatientFacingPropertyValues: $customPatientFacingPropertyValues
      ) {
        ...QualifiedFeature
      }
    }
    ${QualifiedFeature}
  `,
  PanelFragment: Panel,
  QualiferSearchQuery: gql`
    query QualifierSearchQuery(
      $feature: ID!
      $relation: ID
      $search: String
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = false
    ) {
      qualifierSearch(
        search: $search
        features: [$feature]
        relation: $relation
        limit: 1000
      ) {
        ...Qualifier
      }
    }
    ${Qualifier}
  `,
  FreeTextToPatientCase: gql`
    query freeTextToPatientCase(
      $text: String
      $qfLight: Boolean = true
      $includeFeatureRange: Boolean = false
    ) {
      freeTextToPatientCase(text: $text) {
        patientFindings {
          ...GroupedFeatureResponse
        }
        debug
      }
    }
    ${GroupedFeatureResponse}
  `,

  RunLoaderStatus: gql`
    query RunLoadersStatusQuery($env: String) {
      runLoadersStatus(env: $env) {
        id
        filter
        startedat
        shardtotal
        version
        commitserial
        revision
        codeversion
        state
        url
        runningEnv
        loaders {
          shard
          shardtotal
          processed
          processedtotal
          date
          lastevent
          state
        }
      }
    }
  `,
  MetricsPreload: gql`
    mutation metricsPreload(
      $env: String
      $filter: String
      $shards: Int
      $taskversion: String
    ) {
      metricsPreload(
        env: $env
        filter: $filter
        shards: $shards
        taskversion: $taskversion
      ) {
        success
      }
    }
  `,
  SetVersionState: gql`
    mutation setVersionState(
      $env: String
      $version: String
      $state: GVersionState
    ) {
      setVersionState(env: $env, version: $version, state: $state)
    }
  `,
  SetVersionRunningEnv: gql`
    mutation setVersionRunningEnv(
      $env: String
      $version: String
      $runningEnv: [String]
    ) {
      setVersionRunningEnv(
        env: $env
        version: $version
        runningEnv: $runningEnv
      )
    }
  `,
  QFMetrics: gql`
    query metricsQF($qfs: [QualifiedFeatureInput] = []) {
      metricsQF(qfs: $qfs) {
        id
        incidence
        prevalence
        incidenceDescription
        incidenceSource
        prevalenceDescription
        prevalenceSource
      }
    }
  `,
  QFSCorrlate: gql`
    query qfsCorrelate(
      $qfs1: [QualifiedFeatureInput] = []
      $qfs2: [QualifiedFeatureInput] = []
    ) {
      qfsCorrelate(qfs1: $qfs1, qfs2: $qfs2) {
        qfs1 {
          name
        }
        qfs2 {
          name
        }
        correlate
      }
    }
  `,
  QFSCorrlateLists: gql`
    query qfsCorrelateLists(
      $qfsList1: [[QualifiedFeatureInput]] = []
      $qfsList2: [[QualifiedFeatureInput]] = []
    ) {
      qfsCorrelateLists(qfsList1: $qfsList1, qfsList2: $qfsList2) {
        qfs1 {
          name
        }
        qfs2 {
          name
        }
        correlate
      }
    }
  `,
  LambdaTests: gql`
    mutation lambdaTests(
      $devEnv: String
      $project: String
      $headline: String
      $cases: String
      $multiFilter: String
      $tagsFilter: String
      $taskVersion: String
      $modelVersion: String
      $filter: String
      $panel: String
      $keepFunctions: Int
      $disableLogs: Int
      $concurrency: Int
      $loops: String
    ) {
      lambdaTests(
        devEnv: $devEnv
        project: $project
        headline: $headline
        cases: $cases
        multiFilter: $multiFilter
        tagsFilter: $tagsFilter
        taskVersion: $taskVersion
        modelVersion: $modelVersion
        filter: $filter
        panel: $panel
        keepFunctions: $keepFunctions
        disableLogs: $disableLogs
        concurrency: $concurrency
        loops: $loops
      ) {
        success
      }
    }
  `,
  CreateClinic: gql`
    mutation createClinic(
      $name: String
      $config: JSONObject
      $displayName: String
    ) {
      user {
        createClinic(name: $name, config: $config, displayName: $displayName) {
          id
          name
          config
          displayName
        }
      }
    }
  `,
  RenameClinic: gql`
    mutation renameClinic($id: ID, $newName: String, $newDisplayName: String) {
      clinic(id: $id) {
        renameClinic(newName: $newName, newDisplayName: $newDisplayName) {
          status
        }
      }
    }
  `,
  SetClinicConfig: gql`
    mutation setClinicConfig($id: ID, $config: Config) {
      clinic(id: $id) {
        setConfig(config: $config) {
          status
        }
      }
    }
  `,
  AddUserToClinic: gql`
    mutation addUser($clinicId: ID, $user: ID, $role: USER_ROLES) {
      clinic(id: $clinicId) {
        addUser(user: $user, role: $role) {
          status
        }
      }
    }
  `,
  RemoveUserFromClinic: gql`
    mutation delUser($clinicId: ID, $user: ID) {
      clinic(id: $clinicId) {
        delUser(user: $user) {
          status
        }
      }
    }
  `,
  GetUsersOfClinic: gql`
    query getUsersOfClinic($id: ID) {
      clinic(id: $id) {
        users {
          email
          role
        }
      }
    }
  `,
  GetUserData: gql`
    query getUserData($id: ID) {
      clinic(id: $id) {
        userData {
          email
          role
        }
      }
    }
  `,
  UserClinics: gql`
    query userClinics($onlyMyClinics: Boolean) {
      user {
        clinics(onlyMyClinics: $onlyMyClinics) {
          config
          id
          name
          displayName
        }
      }
    }
  `,
  isClinicNameAvailable: gql`
    query isClinicNameAvailable($name: String) {
      user {
        isClinicNameAvailable(name: $name)
      }
    }
  `,
  RecentCodes: gql`
    query recentCodes(
      $id: ID
      $daysPrior: Int = 7
      $limit: Int = 50
      $isFinished: Boolean = true
      $exclusiveStartId: String
      $exclusiveStartDate: Float
    ) {
      clinic(id: $id) {
        recentCodes(
          daysPrior: $daysPrior
          limit: $limit
          isFinished: $isFinished
          exclusiveStartId: $exclusiveStartId
          exclusiveStartDate: $exclusiveStartDate
        ) {
          ...ShareCodeDataFragment
          patientProperties
          visitStatus
        }
      }
    }
    ${ShareCodeDataFragment}
  `,
  RecentVisits: gql`
    query recentVisits(
      $id: ID
      $limit: Int = 50
      $isFinished: Boolean = true
      $startKey: StartKey
      $order: Order
    ) {
      clinic(id: $id) {
        recentVisits(
          limit: $limit
          isFinished: $isFinished
          startKey: $startKey
          order: $order
        ) {
          visits {
            ...ShareCodeDataFragment
            patientProperties
            visitStatus
          }
          startKey
        }
      }
    }
    ${ShareCodeDataFragment}
  `,
  RecentVisitsFiltered: gql`
    query filteredVisits(
      $id: ID
      $filter: FilterInput
      $startKey: StartKey
      $sortKey: String
      $limitRequested: Int
      $isFinished: Boolean
      $order: Order
    ) {
      clinic(id: $id) {
        filteredVisits(
          filter: $filter
          limitRequested: $limitRequested
          startKey: $startKey
          sortKey: $sortKey
          isFinished: $isFinished
          order: $order
        ) {
          visits {
            ...ShareCodeDataFragment
            visitStatus
            visitStatusStructure {
              ...visitStatusFragment
            }
            patientProperties
          }
          startKey
          count
        }
      }
    }
    ${ShareCodeDataFragment}
    ${visitStatusFragment}
  `,
  getCode: gql`
    query getCode($id: ID, $code: String) {
      clinic(id: $id) {
        getCodeByDigits(code: $code) {
          ...ShareCodeDataFragment
          visitStatusStructure {
            ...visitStatusFragment
          }
          patientProperties
        }
      }
    }
    ${ShareCodeDataFragment}
    ${visitStatusFragment}
  `, //TODO: create getCaseByCode()
  getCodeByShareId: gql`
    query getCodeById($clinicId: ID, $shareId: ID, $ehrid: ID) {
      clinic(id: $clinicId) {
        getCodeById(shareid: $shareId, ehrid: $ehrid) {
          id
          digitCode
          shareid
          date
        }
      }
    }
  `,
  deleteCode: gql`
    mutation deleteCode($clinicId: ID, $code: String) {
      clinic(id: $clinicId) {
        deleteCode(code: $code) {
          status
        }
      }
    }
  `,
  addUserComment: gql`
    mutation addUserComment($clinicId: ID, $code: String, $comment: String) {
      clinic(id: $clinicId) {
        addUserComment(code: $code, comment: $comment) {
          status
        }
      }
    }
  `,
  ClinicData: gql`
    query clinicData($id: ID) {
      clinic(id: $id) {
        data {
          config
          mergedConfiguration
          name
          id
          displayName
        }
      }
    }
  `,
  GetClinicConfig: gql`
    query getClinicConfig($id: ID) {
      clinic(id: $id) {
        data {
          config
          mergedConfiguration
          name
        }
      }
    }
  `,
  ClinicByName: gql`
    query ClinicByName($name: String) {
      clinicByName(name: $name) {
        data {
          config
          mergedConfiguration
          name
          displayName
          id
        }
      }
    }
  `,
  MakeClinicShortCode: gql`
    mutation makeClinicShortCode($id: ID, $shareid: String) {
      clinic(id: $id) {
        updateFinished(shareid: $shareid) {
          ...ShareCodeDataFragment
        }
      }
    }
    ${ShareCodeDataFragment}
  `,
  CopyCaseToClinic: gql`
    mutation copyCaseToClinic(
      $clinicId: ID
      $caseId: String
      $destClinicId: String
    ) {
      clinic(id: $clinicId) {
        copyCaseToClinic(caseId: $caseId, destClinicId: $destClinicId) {
          status
        }
      }
    }
  `,
  MarkCodeAsSeen: gql`
    mutation updateSeenByUser(
      $clinicId: ID
      $code: String
      $user: String
      $isSeen: Boolean
    ) {
      clinic(id: $clinicId) {
        updateSeenByUser(code: $code, user: $user, isSeen: $isSeen) {
          ...ShareCodeDataFragment
        }
      }
    }
    ${ShareCodeDataFragment}
  `,
  deleteClinic: gql`
    mutation deleteClinic($id: ID) {
      clinic(id: $id) {
        deleteClinic {
          status
        }
      }
    }
  `,
  PatientSummaryByCase: gql`
    query PatientSummaryByCase(
      $patientCase: PatientCaseInput
      $diseases: [QualifiedFeatureInput]
      $suggestions: [QualifiedFeatureInput]
      $options: SummaryOptions
      $summaryOptions: SummaryOptionsInput
    ) {
      patientSummaryByCase(
        patientCaseInput: $patientCase
        diseases: $diseases
        suggestions: $suggestions
        options: $options
        summaryOptions: $summaryOptions
      ) {
        data {
          ...PatientFirstSummary
        }
      }
    }
    ${PatientFirstSummaryFragment}
  `,
  metricsPaths: gql`
    query metricsPaths(
      $causeids: [String]
      $effectids: [String]
      $causeqfs: [QualifiedFeatureInput]
      $effectqfs: [QualifiedFeatureInput]
      $effectIsDisease: Boolean
      $patient: PatientCaseInput
    ) {
      metricsPaths(
        causeids: $causeids
        effectids: $effectids
        causeqfs: $causeqfs
        effectqfs: $effectqfs
        effectIsDisease: $effectIsDisease
        patient: $patient
      ) {
        causeId
        effectId
        nodes {
          name
          desc
        }
        edges {
          type
          prob
          incidence
          lift
          stats {
            rate
            unit
            lastCommitDate
            ruleValue {
              id
              ruleValueId
              type
              citationsObj {
                id
                vancouver
              }
              exactCitation
            }
          }
        }
        prob
      }
    }
  `,
  Conversation: {
    Undo: gql`
      query undoConversation(
        $context: ConversationContext
        $debug: Boolean = false
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          state(
            context: $context
            options: $options
            conversationOptions: $conversationOptions
          ) {
            undo {
              gender
              entropy @include(if: $debug)
              context
              nextSuggestions @include(if: $debug) {
                ...ConversationNextSuggestions
              }
            }
          }
        }
      }
      ${ConversationNextSuggestionsFragment}
      ${QualifiedFeature}
      ${PatientSuggestionMIExplanationsFragment}
    `,
    Abandoned: gql`
      query AbandonedPatientConversation(
        $context: ConversationContext
        $debug: Boolean = false
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
        $saveToCase: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
        $abandonedStatus: SubStatus
      ) {
        conversation {
          state(
            context: $context
            saveToCase: $saveToCase
            options: $options
            conversationOptions: $conversationOptions
          ) {
            abandoned(abandonedStatus: $abandonedStatus) {
              ...ConversationState
              ...ConversationStateNextResponse
            }
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${ConversationStateFragment}
    `,
    GetRecommendations: gql`
      query getRecommendations(
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
        $saveToCase: Boolean = false
        $patientCaseId: String
        $clinicId: String
      ) {
        conversation {
          start(
            existingCaseId: $patientCaseId
            options: $options
            conversationOptions: $conversationOptions
            clinicId: $clinicId
            saveToCase: $saveToCase
          ) {
            recommendations {
              ...ConversationRecommendation
            }
            navigationRecommendation {
              ...ConversationRecommendation
            }
          }
        }
      }
      ${ConversationRecommendationFragment}
    `,
    NextResponse: gql`
      query nextResponse(
        $context: ConversationContext
        $debug: Boolean = false
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
        $saveToCase: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        ...BackendModelInfo
        conversation {
          state(
            context: $context
            saveToCase: $saveToCase
            options: $options
            conversationOptions: $conversationOptions
          ) {
            ...ConversationStateNextResponse
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${LoadedModelInfoFragment}
    `,
    AddFindings: gql`
      query addPatientAnswerConversation(
        $context: ConversationContext
        $answer: ConversationAnswer
        $debug: Boolean = false
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
        $saveToCase: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        ...BackendModelInfo
        conversation {
          state(
            context: $context
            saveToCase: $saveToCase
            options: $options
            conversationOptions: $conversationOptions
          ) {
            addAnswer(answer: $answer) {
              ...ConversationStateNextResponse
            }
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${LoadedModelInfoFragment}
    `,
    DoExchange: gql`
      query doPatientExchange(
        $context: ConversationContext
        $patient: [GroupedFeature]
        $patientVolunteered: [Int] = []
        $missingInCase: MissingInCaseAction = SKIP
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $debug: Boolean = true
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          state(
            context: $context
            saveToCase: false
            options: $options
            conversationOptions: $conversationOptions
          ) {
            doExchange(
              patient: $patient
              patientVolunteered: $patientVolunteered
              missingInCase: $missingInCase
            ) {
              ...ConversationExchangeResult
            }
          }
        }
      }
      ${ConversationExchangeResultFragment}
    `,
    DoExchangeResult: gql`
      query doPatientExchangeResult(
        $context: ConversationContext
        $patient: [GroupedFeature]
        $patientVolunteered: [Int] = []
        $missingInCase: MissingInCaseAction = SKIP
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $debug: Boolean = true
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          state(
            context: $context
            saveToCase: false
            options: $options
            conversationOptions: $conversationOptions
          ) {
            entropy
            doExchange(
              patient: $patient
              patientVolunteered: $patientVolunteered
              missingInCase: $missingInCase
            ) {
              ...ConversationExchangeResult
            }
            ddClusters {
              clustersToConsider {
                title {
                  name
                }
                crIds
              }
              clinicalReasoning {
                id
                disease {
                  id
                  name
                  qualifiers {
                    name
                  }
                }
                scores {
                  postClusterAdjustment
                  sortType
                }
                urgency
              }
            }
            urgent {
              urgencyLevelId
              urgent
            }
            nextSuggestions {
              diseases {
                disease {
                  id
                  name
                }
                prob
              }
              suggestions {
                perDiseaseMetrics {
                  suggestion {
                    id
                  }
                  disease {
                    id
                  }
                  findingGivenDisease
                }
                suggestion {
                  id
                  name
                  qualifiers {
                    name
                    relation {
                      id
                    }
                    range {
                      low
                      high
                      units
                    }
                  }
                }
                findingEntropy
                findingLeak
                findingProbability
                category
                mutualInformation
                mutualInformationUrgencyDifferentiation
              }
            }
          }
        }
      }
      ${ConversationExchangeResultFragment}
    `,
    BuildCase: gql`
      query BuildCaseFromConversation(
        $context: ConversationContext
        $buildCaseOptions: BuildCaseOptions
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
        $caseId: ID
      ) {
        conversation {
          buildSavedCase(
            context: $context
            buildCaseOptions: $buildCaseOptions
            options: $options
            conversationOptions: $conversationOptions
            caseId: $caseId
          ) {
            findings {
              ...GroupedFeatureResponse
            }
          }
        }
      }
      ${GroupedFeatureResponse}
    `,
    SaveAs: gql`
      query SaveAsPatientConversation(
        $context: ConversationContext
        $conversationOptions: ConversationOptionsInput
        $readOnly: Boolean
      ) {
        conversation {
          saveAsNewCase(
            context: $context
            conversationOptions: $conversationOptions
            readOnly: $readOnly
          ) {
            caseId
          }
        }
      }
    `,
    Persist: gql`
      query PersistConversation(
        $context: ConversationContext
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          state(
            context: $context
            options: $options
            conversationOptions: $conversationOptions
            saveToCase: true
          ) {
            context
          }
        }
      }
    `,
    PersistById: gql`
      query PersistConversationById(
        $existingCaseId: String
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          start(
            existingCaseId: $existingCaseId
            options: $options
            conversationOptions: $conversationOptions
            saveToCase: true
          ) {
            persist {
              context
            }
          }
        }
      }
    `,
    Start: gql`
      query StartConversation(
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $debug: Boolean = false
        $patientCaseId: String = null
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
        $clinicId: String = null
      ) {
        ...BackendModelInfo
        conversation {
          start(
            existingCaseId: $patientCaseId
            options: $options
            conversationOptions: $conversationOptions
            clinicId: $clinicId
          ) {
            caseId
            ...ConversationStateNextResponse
            exchanges {
              ...ConversationExchange
            }
            ...ConversationState
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${ConversationStateFragment}
      ${ConversationExchangeFragment}
      ${ConversationNextQuestionFragment}
      ${LoadedModelInfoFragment}
    `,
    PatientFirstSummaryById: gql`
      query PatientSummary(
        $id: String
        $options: SummaryOptions
        $summaryOptions: SummaryOptionsInput
        $conversationOptions: ConversationOptions
        $conversationOptionsInput: ConversationOptionsInput
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $clinicId: String = null
      ) {
        patientSummary(
          id: $id
          clinicId: $clinicId
          options: $options
          summaryOptions: $summaryOptions
          conversationOptions: $conversationOptions
          conversationOptionsInput: $conversationOptionsInput
        ) {
          data {
            ...PatientFirstSummary
          }
          recommendations {
            ...ConversationRecommendation
          }
          navigationAdvice {
            ...ConversationRecommendation
          }
          possibleCause {
            ...SummaryDiseaseCluster
          }
          possibleCauses {
            possibleCauses {
              ...SummaryDiseaseCluster
            }
            etag
          }
          problemList {
            ...SummaryProblem
          }
          assessment {
            ...GroundedSummaryText
          }
          workup {
            ...SummaryText
          }
          workupList {
            category
            title
          }
          workupActionItems {
            items {
              category
              title
            }
            ...GeneratedOutput
          }
          validCase {
            isValid
            reason
            etag
          }
          status
          caseEtag
        }
      }
      ${PatientFirstSummaryFragment}
      ${ConversationRecommendationFragment}
      ${SummaryDiseaseCluster}
      ${SummaryProblem}
      ${SummaryTextFragment}
      ${GroundedSummaryTextFragment}
    `,
    PatientFirstSummaryByContext: gql`
      query PatientSummaryByContext(
        $context: ConversationContext
        $options: SummaryOptions
        $conversationOptions: ConversationOptionsInput
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = true
      ) {
        conversation {
          state(
            context: $context
            options: { summaryOptions: $options }
            conversationOptions: $conversationOptions
          ) {
            summary {
              ...PatientFirstSummary
            }
            navigationRecommendation {
              ...ConversationRecommendation
            }
          }
        }
      }
      ${PatientFirstSummaryFragment}
      ${ConversationRecommendationFragment}
    `,
    ConversationNextQuestion: gql`
      query conversationNextQuestion(
        $context: ConversationContext
        $endExchangeIndex: Int = -1
        $qfLight: Boolean = true
        $debug: Boolean = false
        $includeFeatureRange: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
        $panel: ID
      ) {
        conversation {
          state(
            context: $context
            endExchangeIndex: $endExchangeIndex
            options: $options
            conversationOptions: $conversationOptions
            panel: $panel
          ) {
            ...ConversationStateNextQ
          }
        }
      }
      ${ConversationStateNextQFragment}
    `,
    RestoreConversation: gql`
      query restoreConversation(
        $context: ConversationContext
        $qfLight: Boolean = true
        $debug: Boolean = false
        $includeFeatureRange: Boolean = false
        $options: ConversationOptions
        $privateMode: Boolean = false
        $conversationOptions: ConversationOptionsInput
      ) {
        ...BackendModelInfo
        conversation {
          state(
            context: $context
            options: $options
            conversationOptions: $conversationOptions
            privateMode: $privateMode
          ) {
            initialMessages {
              ...ConversationInfoMessage
            }
            exchanges {
              ...ConversationExchange
            }
            ...ConversationStateNextResponse
            ...ConversationState
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${ConversationStateFragment}
      ${ConversationInfoMessageFragment}
      ${ConversationExchangeFragment}
      ${LoadedModelInfoFragment}
    `,
    DebugForLastQuestion: gql`
      query debugForLastQuestion(
        $context: ConversationContext
        $qfLight: Boolean = true
        $debug: Boolean = false
        $includeFeatureRange: Boolean = false
        $options: ConversationOptions
        $privateMode: Boolean = false
        $conversationOptions: ConversationOptionsInput
        $endExchangeIndex: Int = -1
      ) {
        ...BackendModelInfo
        conversation {
          state(
            context: $context
            options: $options
            conversationOptions: $conversationOptions
            privateMode: $privateMode
            endExchangeIndex: $endExchangeIndex
          ) {
            initialMessages {
              ...ConversationInfoMessage
            }
            exchanges {
              ...ConversationExchange
            }
            debugInfo
            ...ConversationStateNextResponse
            ...ConversationState
          }
        }
      }
      ${ConversationStateNextResponseFragment}
      ${ConversationStateFragment}
      ${ConversationInfoMessageFragment}
      ${ConversationExchangeFragment}
      ${LoadedModelInfoFragment}
    `,
    DD: gql`
      query DDFromConversation(
        $context: ConversationContext
        $qfLight: Boolean = true
        $includeFeatureRange: Boolean = false
        $options: ConversationOptions
        $conversationOptions: ConversationOptionsInput
      ) {
        conversation {
          state(
            context: $context

            options: $options
            conversationOptions: $conversationOptions
          ) {
            primaryDDClusters
            ddClusters {
              ...ConversationDD
              debugReport
            }
            urgent {
              urgencyLevelId
            }
            navigationRecommendation {
              ...ConversationRecommendation
            }
          }
        }
      }
      ${ConversationDDFragment}
      ${ConversationRecommendationFragment}
    `,
  },
  BackendModel: gql`
    query backendModel($debug: Boolean = true) {
      ...BackendModelInfo
    }
    ${LoadedModelInfoFragment}
  `,
  FeatureById: gql`
    query featureByID(
      $id: ID!
      $qfLight: Boolean = false
      $includeFeatureRange: Boolean = true
    ) {
      feature(id: $id) {
        ...Feature
      }
    }
    ${Feature}
  `,
  GetPolicies: gql`
    query getPolicies {
      policies {
        id
        name
      }
    }
  `,
  GetPanels: gql`
    query getPanels {
      panels {
        id
        name
      }
    }
  `,
};
